import { useProduct } from '../../index.js'
import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { Select } from '../../../../shared/ui/Select/index.js'
import { Tooltip } from '../../../../shared/ui/Tooltip/index.js'

import { COLOR_SCHEME_REGISTRY, COLOR_SCHEME_ORDER, COLOR_SCHEME, ZONE_KEYS, COMPARE_KEYS } from '../../constants/colorSchemes.js'

import styles from './InfoModeController.module.scss'

// ── Per-scheme legend sub-components ───────────────────────────────────────

/**
 * ZONES legend: 4 horizontal swatches, each with a label below.
 * day / night / neutral / outdoor
 */
function ZonesLegend() {
  const { t } = useI18n()
  const { viewerState, viewerActions } = useProduct()
  const { infoLegendHoveredKey, infoHoveredPathKey } = viewerState
  const { setInfoLegendHoveredKey } = viewerActions

  const items = ZONE_KEYS.map((key) => ({
    key,
    cssVar: `--bim-color-zone-${key}`,
    label: t(`product.zone_${key}`),
  }))

  const activeKey = (infoLegendHoveredKey?.scheme === 'zones' ? infoLegendHoveredKey.key : null)
    ?? (infoHoveredPathKey?.scheme === 'zones' ? infoHoveredPathKey.key : null)

  return (
    <div className={styles.zonesLegendBox}>
      {items.map(({ key, cssVar, label }) => {
        const isDimmed = activeKey != null && key !== activeKey
        return (
          <div
            key={cssVar}
            className={styles.legendItem}
            onMouseEnter={() => setInfoLegendHoveredKey({ scheme: 'zones', key })}
            onMouseLeave={() => setInfoLegendHoveredKey(null)}
          >
            <span
              className={styles.swatch}
              style={{ background: `var(${cssVar}${isDimmed ? '-muted' : ''})` }}
            />
            <span className={styles.swatchLabel}>{label}</span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * COMPARE legend: 10 horizontal swatches (p0–p9).
 * Three labeled anchors (below / avg / above), rest tooltip-only.
 */
function CompareLegend() {
  const { t } = useI18n()
  const { viewerState, viewerActions } = useProduct()
  const { infoLegendHoveredKey, infoHoveredPathKey } = viewerState
  const { setInfoLegendHoveredKey } = viewerActions

  const swatches = COMPARE_KEYS.map((key) => ({
    key,
    cssVar: `--bim-color-compare-${key}`,
  }))

  const activeKey = (infoLegendHoveredKey?.scheme === 'compare' ? infoLegendHoveredKey.key : null)
    ?? (infoHoveredPathKey?.scheme === 'compare' ? infoHoveredPathKey.key : null)

  return (
    <div className={styles.compareLegendBox}>
      <div className={styles.compareSwatches}>
        {swatches.map(({ key, cssVar }) => {
          const isDimmed = activeKey != null && key !== activeKey
          return (
            <Tooltip key={cssVar} title={t(`product.compare_tooltip_${key}`)}>
              <span
                className={styles.compareSwatch}
                style={{ background: `var(${cssVar}${isDimmed ? '-muted' : ''})` }}
                onMouseEnter={() => setInfoLegendHoveredKey({ scheme: 'compare', key })}
                onMouseLeave={() => setInfoLegendHoveredKey(null)}
                tabIndex={0}
              />
            </Tooltip>
          )
        })}
      </div>
      <div className={styles.compareLabels}>
        <span>{t('product.compare_below')}</span>
        <span>{t('product.compare_avg')}</span>
        <span>{t('product.compare_above')}</span>
      </div>
    </div>
  )
}

/**
 * BRIGHTNESS legend:
 *   [--gray--] [--blue--] [--------yellow gradient---------]
 *   No window  Below norm                   Very well lit →|
 *
 * Both directions:
 *  swatch→paths: hover/mousemove on segments → setInfoLegendHoveredKey dims non-matching paths
 *  path→swatch:  infoHoveredPathKey.scheme === 'brightness' → highlight matching segment / tick
 */
function BrightnessLegend() {
  const { t } = useI18n()
  const { viewerState, viewerActions } = useProduct()
  const { infoHoveredPathKey } = viewerState
  const { setInfoLegendHoveredKey } = viewerActions

  // path→swatch: which segment/bucket is the currently hovered plan path in?
  const pathBucket = infoHoveredPathKey?.scheme === COLOR_SCHEME.BRIGHTNESS ? infoHoveredPathKey.key : null

  // swatch→paths: track cursor position across gradient to derive 0–9 bucket
  const handleGradientMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const bucket = Math.min(9, Math.max(0, Math.floor(((e.clientX - rect.left) / rect.width) * 10)))
    setInfoLegendHoveredKey({ scheme: COLOR_SCHEME.BRIGHTNESS, key: bucket })
  }

  // Position of the tick within the gradient span (only shown for number bucket from path hover)
  const tickLeft = typeof pathBucket === 'number' ? `${(pathBucket + 0.5) / 10 * 100}%` : null

  return (
    <div className={styles.brightnessLegendBox}>
      <div className={styles.brightnessBar}>
        <span
          className={`${styles.brightnessNone}${pathBucket === 'none' ? ` ${styles.brightnessSegmentActive}` : ''}`}
          style={{ background: 'var(--bim-color-brightness-none, #d0d0d0)' }}
          onMouseEnter={() => setInfoLegendHoveredKey({ scheme: COLOR_SCHEME.BRIGHTNESS, key: 'none' })}
          onMouseLeave={() => setInfoLegendHoveredKey(null)}
        />
        <span
          className={`${styles.brightnessLow}${pathBucket === 'low' ? ` ${styles.brightnessSegmentActive}` : ''}`}
          style={{ background: 'var(--bim-color-brightness-low)' }}
          onMouseEnter={() => setInfoLegendHoveredKey({ scheme: COLOR_SCHEME.BRIGHTNESS, key: 'low' })}
          onMouseLeave={() => setInfoLegendHoveredKey(null)}
        />
        <span
          className={styles.brightnessGradient}
          style={{ background: 'linear-gradient(to right, #ffffff, var(--bim-color-brightness-high))' }}
          onMouseMove={handleGradientMouseMove}
          onMouseLeave={() => setInfoLegendHoveredKey(null)}
        >
          {tickLeft != null && (
            <span className={styles.brightnessTick} style={{ left: tickLeft }} />
          )}
        </span>
      </div>
      <div className={styles.brightnessLabels}>
        <span>{t('product.brightness_none')}</span>
        <span>{t('product.brightness_low')}</span>
        <span>{t('product.brightness_high')}</span>
      </div>
    </div>
  )
}

// ── InfoModeController ────────────────────────────────────────────────────

/**
 * InfoModeController — controller panel for InfoMode.
 *
 * Contains:
 *  1. Color scheme <Select> (only when >1 scheme is available)
 *  2. One-line description of the active scheme
 *  3. Per-scheme legend (ZONES / COMPARE / BRIGHTNESS; none for MONO / TYPES)
 *
 * Reads/writes via ProductContext (viewerState.activeColorScheme,
 * viewerActions.setActiveColorScheme, capabilities.availableColorSchemes).
 */
export function InfoModeController() {
  const { t } = useI18n()
  const { capabilities, viewerState, viewerActions } = useProduct()
  const { availableColorSchemes } = capabilities
  const { activeColorScheme } = viewerState
  const { setActiveColorScheme } = viewerActions

  const schemeOptions = COLOR_SCHEME_ORDER
    .filter((s) => availableColorSchemes.includes(s))
    .map((s) => ({ value: s, label: t(COLOR_SCHEME_REGISTRY[s].labelKey) }))

  let legend = null
  if (activeColorScheme === COLOR_SCHEME.ZONES)      legend = <ZonesLegend />
  if (activeColorScheme === COLOR_SCHEME.COMPARE)    legend = <CompareLegend />
  if (activeColorScheme === COLOR_SCHEME.BRIGHTNESS) legend = <BrightnessLegend />

  return (
    <div className={styles.infoModeContainer}>
      <span className={styles.selectLabelText}>{t('product.color_schemes_label')}</span>
      {schemeOptions.length > 1 ? (
        <div className={styles.selectLabel}>
          <Select
            value={activeColorScheme}
            onChange={setActiveColorScheme}
            options={schemeOptions}
          />
        </div>
      ) : (
        <span className={styles.singleSchemeLabel}>
          {schemeOptions[0] ? schemeOptions[0].label : null}
        </span>
      )
    }

      <p className={styles.schemeDesc}>
        {t(`product.${activeColorScheme}_desc`)}
      </p>

      {legend}
    </div>
  )
}
